/**
 * Modal Field Adapter for Grades
 * Transforms Grade Field Registry into Modal field configurations
 */

import { FieldConfig } from '../../../components/modals/ModalFieldRenderer';
import { Grade } from '../../../services/api/domains/grades/types';
import { GRADE_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { formatTimestamp } from '../../../utils/dateConversion';

/**
 * Grade field options (empty interface for consistency)
 */
export interface GradeFieldOptions {
  // No relational data needed for Grades
}

/**
 * Create a standard field configuration
 * @param field - Field definition from registry
 * @returns FieldConfig for modal
 */
function createStandardField(
  field: BaseFieldDefinition<Grade>
): FieldConfig<Grade> {
  let fieldType: 'text' | 'textarea' | 'number' | 'date' | 'readonly' = 'text';

  if (field.isReadOnly) {
    fieldType = 'readonly';
  } else if (field.renderConfig?.rows && field.renderConfig.rows > 1) {
    fieldType = 'textarea';
  } else if (field.dataType === 'number') {
    fieldType = 'number';
  } else if (field.dataType === 'date') {
    fieldType = 'date';
  }

  return {
    key: field.key as keyof Grade,
    label: field.label,
    type: fieldType,
    getValue: (item: Grade) => {
      const value = item[field.key as keyof Grade];

      // Format boolean
      if (field.dataType === 'boolean') {
        if (value === null || value === undefined) return '';
        return value ? 'Yes' : 'No';
      }

      return value || '';
    },
    setValue: field.isReadOnly
      ? undefined
      : (item: Grade, val: unknown) => ({
          ...item,
          [field.key]: val || undefined,
        }),
    isAlwaysDisabled: field.isReadOnly,
    isRequired: field.isRequired,
    tooltipText: field.description,
    rows: field.renderConfig?.rows,
    formatter:
      field.dataType === 'date' && field.isReadOnly
        ? formatTimestamp
        : undefined,
  };
}

/**
 * Transform Grade Field Registry to Modal field configurations
 * @param apiData - Optional API data for relational fields (not used for Grades)
 * @returns Array of FieldConfig for modal
 */
export function toModalFields(
  _apiData: GradeFieldOptions = {}
): FieldConfig<Grade>[] {
  const fields: FieldConfig<Grade>[] = [];

  for (const field of GRADE_FIELDS_REGISTRY) {
    // Skip grade_id - it's auto-generated and shown separately
    if (field.key === 'grade_id') continue;

    // Handle standard fields
    if (field.dataType !== 'array' && field.dataType !== 'object') {
      fields.push(createStandardField(field));
    }
  }

  return fields;
}

/**
 * Get fields that should always be disabled in edit mode
 * @returns Array of field keys
 */
export function getAlwaysDisabledFields(): string[] {
  return GRADE_FIELDS_REGISTRY.filter(f => f.isReadOnly).map(
    f => f.key as string
  );
}
